import { orderActionTypes } from '../order-action-types';
import axios from 'axios';
import { BASE_URL } from '../../../../config/environmentConfig';
import { ORDERS_ROUTES } from '../../../utils/constatns';
import { sendSentryMessage } from '../../../utils/sentry-helper';
import * as Sentry from '@sentry/react';
import { getFormattedDate, makeSearchParams } from '../../../utils/utilities';
import * as React from 'react';
import { IOrderAction, IOrderContextState } from '../order-interfaces';
import { IOrderListPageParams } from '../../../views/orders/OrdersList';
import { IOrderItemsParams } from '../../../components/orders/OrderItem';
import { MRT_PaginationState, MRT_SortingState } from 'material-react-table';
import { saveFavoriteFiltersToStorage } from '../../../utils/filter-helpers';
import download from 'downloadjs';
import { getSelectedStatuses } from '../../../utils/linksHelper';
import { ISelectorData } from '../../../components/application/NewApplication';
import { GET, hateoasRequest } from '../../store-helper';
import { IOrderHateoasLinks } from '../../../components/orders/OrderDetails';

export interface IOrderFilters {
	status?: string[],
	from?: string | Date,
	to?: string | Date,
	placedFrom?: string | Date,
	placedTo?: string | Date,
	timezone?: string,
	isMIT?: string,
	sid?: string,
	pid?: string,
	orderId?: string,
	externalOrderId?: string,
	email?: string,
	firstName?: string,
	lastName?: string,
	query?: string,
	favoriteFilters?: string[],
	bin?: string | number,
	last4?: string | number,
	sort?: string,
	sortField?: string,
	pageNumber?: string | number,
	pageSize?: string | number,
	types?: string,
	mid?: string,
}

interface IGetOrdersListQueryParams {
	isExport?: boolean,
	filterData: IOrderFilters,
	state: IOrderContextState,
}

export const getOrdersListQueryParamsV2 = ({
	isExport = false,
	filterData,
	state
}: IGetOrdersListQueryParams): { [p: string]: string | string[] | number | boolean } => {
	const params: { [key: string]: string | string[] | number | boolean } = {};
	const placedFrom = filterData?.placedFrom ? getFormattedDate(filterData?.placedFrom, 'yyyy-MM-dd HH:mm:ss') : undefined;
	const placedTo = filterData?.placedTo ? getFormattedDate(filterData?.placedTo, 'yyyy-MM-dd HH:mm:ss') : undefined;
	const from = filterData?.from ? getFormattedDate(filterData?.from, 'yyyy-MM-dd HH:mm:ss') : undefined;
	const to = filterData?.to ? getFormattedDate(filterData?.to, 'yyyy-MM-dd HH:mm:ss') : undefined;

	if (filterData?.mid) {
		params.mid = filterData?.mid;
	}

	Object.keys(filterData).forEach(key => {
		if (key === 'isMIT' && filterData[key] !== '') {
			params[key] = filterData[key] === 'MIT' ? 'true' : 'false';
		} else {
			params[key] = filterData[key];

			params.pageNumber = isExport ? 1 : Number(filterData.pageNumber) + 1;
			params.pageSize = isExport ? filterData.pageSize : filterData.pageSize;
			params.sort = filterData?.sort;
			params.sortField = filterData?.sortField;

			if (placedFrom && placedTo) {
				params.placedFrom = placedFrom;
				params.placedTo = placedTo;
			}

			if (from && to) {
				params.from = from;
				params.to = to;
			}

			if (filterData?.status?.length) {
				const newStatus = [...(filterData?.status || [])];
				const chargebackStatus = state?.orderStatuses?.find(el => el.label.toLowerCase() === 'chargeback')?.value;

				if (newStatus?.includes(chargebackStatus)) {
					params.status = newStatus.filter(el => el !== chargebackStatus);
					params.types = 'chargeback';
				} else {
					params.status = filterData?.status;
				}
			}
		}

	});

	return params;
};

export const fetchOrdersList = async (
	dispatch: React.Dispatch<IOrderAction>,
	searchParams: any,
	setRefreshFilters: (value: boolean) => void
): Promise<void> => {
	try {
		dispatch({ type: orderActionTypes.SET_ORDER_LIST_DATA_LOADING, payload: true });

		const data = await GET({
			pathname: `${BASE_URL.ORDERS}/${ORDERS_ROUTES.ORDERS}`,
			params: searchParams
		});

		setRefreshFilters(false);
		dispatch({
			type: orderActionTypes.FETCH_ORDER_TABLE_DATA,
			payload: data?.orders?.rows ?? [],
			totalItemCount: data?.orders?.totalItemCount,
			links: data?._links
		});
	} catch (e) {
		setRefreshFilters(false);
		sendSentryMessage({ name: `GET Fetch order table data error`, context: e });
		console.log('fetchOrdersList', e);
		dispatch({ type: orderActionTypes.FETCH_ORDER_TABLE_DATA, payload: [], totalItemCount: 0, links: [] });
	}
};

export const exportCSVOrderTable = async (
	dispatch: React.Dispatch<IOrderAction>,
	chosenMerchant: string,
	searchParams: string
): Promise<void> => {
	try {
		dispatch({ type: orderActionTypes.START_EXPORT_CSV_LOADING });

		let search = searchParams;
		if (chosenMerchant) {
			search += `&mid=${chosenMerchant}`;
		}

		const res = await axios(`${BASE_URL.ORDERS}/${ORDERS_ROUTES.ORDERS}/export${search}`, );

		const blob = new Blob([res.data], { type: 'text/csv' });
		download(blob, `Orders.csv`);
		dispatch({ type: orderActionTypes.END_EXPORT_CSV_LOADING });
	} catch (e) {
		console.log(e);
		dispatch({ type: orderActionTypes.END_EXPORT_CSV_LOADING });
	}
};


export const fetchOrderTableData = async (dispatch: React.Dispatch<IOrderAction>, params: IOrderListPageParams): Promise<void> => {
	try {
		dispatch({ type: orderActionTypes.START_LOADING });
		const res = await axios(`${BASE_URL.ORDERS}/${ORDERS_ROUTES.ORDERS}`, {
			params,
			paramsSerializer: params => makeSearchParams(params)
		});

		dispatch({ type: orderActionTypes.FETCH_ORDER_TABLE_DATA, payload: res.data.orders?.rows ?? [], totalItemCount: res?.data?.orders?.totalItemCount });
	} catch (e) {
		sendSentryMessage({ name: `GET Fetch order table data error`, context: e });
		console.log(e);
		dispatch({ type: orderActionTypes.FETCH_ACTIVITY_LISTS_FAIL });
	}
};

export const fetchOrderStatuses = async (dispatch: React.Dispatch<IOrderAction>): Promise<ISelectorData[]> => {
	dispatch({ type: orderActionTypes.SET_ORDER_STATUSES_LOADING, payload: true });
	try {
		const data = await GET({
			pathname: `${BASE_URL.ORDERS}/lists`,
		});

		const newStatuses = [];
		if (data?.orderStatuses) {
			Object.entries(data.orderStatuses || {})?.forEach(([key, value]) => {
				newStatuses.push({
					value: String(key),
					label: value,
				});
			});
			const chargeback = {
				value: String(Object.keys(data?.orderStatuses || {})?.length),
				label: 'Chargeback'
			};
			newStatuses.push(chargeback);
		}
		// console.log('newStatuses', newStatuses);
		dispatch({ type: orderActionTypes.FETCH_ORDER_STATUSES, payload: newStatuses });
		return newStatuses;
	} catch (e) {
		sendSentryMessage({ name: `GET Fetch order statuses error`, context: e });
		console.log('fetchOrderStatuses error', e);
		dispatch({ type: orderActionTypes.SET_ORDER_STATUSES_LOADING, payload: false });
	}
};

export const setOrderDataToDefault = (dispatch: React.Dispatch<IOrderAction>): void => {
	dispatch({ type: orderActionTypes.SET_ORDER_TABLE_DATA_DEFAULT });
};

interface ISetRefundItems {
	dispatch: React.Dispatch<IOrderAction>,
	orderId: string,
	params: IOrderItemsParams,
	errorModal: (responseCode?: string, responseMessage?: string) => void,
	closeModal: () => void,
	refundAction: IOrderHateoasLinks,
}

export const setRefundItems = async ({ dispatch, orderId, params, errorModal, closeModal, refundAction }: ISetRefundItems): Promise<void> => {
	try {
		dispatch({ type: orderActionTypes.START_REFUND_ITEMS_LOADING });

		await hateoasRequest({
			actionData: refundAction,
			pathname: BASE_URL.ORDERS,
			body: params,
		});

		dispatch({ type: orderActionTypes.END_REFUND_ITEMS_LOADING });
		closeModal();
		await fetchOrderDetailsData(dispatch, orderId);
	} catch (e) {
		closeModal();
		Sentry.setTag('fetch-refund-items-orderId', orderId);
		sendSentryMessage({ name: `POST Fetch refund items error`, context: e });
		console.log('refundItems e', e.response);
		dispatch({ type: orderActionTypes.FETCH_ACTIVITY_LISTS_FAIL });
		errorModal(e?.response?.data?.responseCode, e.response?.data?.responseMessage);
	}
};

export const fetchOrderDetailsData = async (dispatch: React.Dispatch<IOrderAction>, orderId: string): Promise<IOrderHateoasLinks[]> => {
	dispatch({ type: orderActionTypes.SET_ORDER_DETAILS_DATA_LOADING, payload: true });
	try {
		const data = await GET({
			pathname: `${BASE_URL.ORDERS}/${ORDERS_ROUTES.ORDERS}/${orderId}`,
		});

		dispatch({ type: orderActionTypes.FETCH_ORDER_DETAILS_DATA, payload: data ?? null });

		return data?.['_links'] || [];
	} catch (e) {
		console.log('fetchOrderTableRowData error', e);
		sendSentryMessage({ name: `GET Fetch order table raw data error`, context: e });
		dispatch({ type: orderActionTypes.FETCH_ORDER_DETAILS_DATA, payload: null });
		dispatch({ type: orderActionTypes.FETCH_ACTIVITY_LISTS_FAIL });
	}
};

export const setOrderDetailsDataToDefault = (dispatch: React.Dispatch<IOrderAction>): void => {
	dispatch({ type: orderActionTypes.SET_ORDER_RETAILS_DATA_TO_DEFAULT });
};

export const setCancelOrder = async (dispatch: React.Dispatch<IOrderAction>, orderId: string): Promise<void> => {
	dispatch({ type: orderActionTypes.START_CANCEL_ORDER_LOADING });
	try {
		await axios.post(`${BASE_URL.ORDERS}/orders/${orderId}/cancel-mit-order-retries`);
		dispatch({ type: orderActionTypes.END_CANCEL_ORDER_LOADING });
	} catch (e) {
		console.log(e);
		dispatch({ type: orderActionTypes.FETCH_ACTIVITY_LISTS_FAIL });
	}
};

export const setOrderSorting = (dispatch: React.Dispatch<IOrderAction>, value: MRT_SortingState): void => {
	dispatch({ type: orderActionTypes.SET_ORDER_TABLE_SORTING, payload: value });
};

export const setOrderPagination = (dispatch: React.Dispatch<IOrderAction>, value: MRT_PaginationState): void => {
	dispatch({
		type: orderActionTypes.SET_ORDER_TABLE_PAGINATION,
		payload: {
			pageNumber: value.pageIndex,
			pageSize: value.pageSize
		}
	});
};

export const setOrderFavoriteFilters = (dispatch: React.Dispatch<IOrderAction>, value: string[]): void => {
	dispatch({ type: orderActionTypes.SET_ORDER_FAVORITE_FILTERS, payload: value });
};

export const savedOrderFilters = (
	dispatch: React.Dispatch<IOrderAction>,
	savedOrderFilters: IOrderFilters,
	orderStatuses?: ISelectorData[]
): void => {

	if (!savedOrderFilters) return;

	switch (savedOrderFilters?.isMIT?.toLowerCase()) {
		case 'true':
		case 'mit':
			savedOrderFilters.isMIT = 'MIT';
			break;
		case 'false':
		case 'cit':
			savedOrderFilters.isMIT = 'CIT';
			break;
		default:
			savedOrderFilters.isMIT = '';
	}

	if (orderStatuses && (savedOrderFilters?.status || savedOrderFilters?.types)) {
		savedOrderFilters.status = getSelectedStatuses(orderStatuses, savedOrderFilters);
	}


	dispatch({ type: orderActionTypes.SET_ORDER_FILTER_STATE, payload: savedOrderFilters });

};

export const setSavedFavoriteOrderFilters = (dispatch: React.Dispatch<IOrderAction>, savedFavoriteOrderFilters: string[]): void => {
	saveFavoriteFiltersToStorage('orders', savedFavoriteOrderFilters);
	if (savedFavoriteOrderFilters) {
		setOrderFavoriteFilters(dispatch, savedFavoriteOrderFilters);
	}
};

export const cleanOrderFilters = (dispatch: React.Dispatch<IOrderAction>, orderStatuses: ISelectorData[]): void => {
	const data = {
		status: [],
		from: '',
		to: '',
		placedFrom: '',
		placedTo: '',
		timezone: 'UTC',
		isMIT: '',
		sid: '',
		pid: '',
		orderId: '',
		externalOrderId: '',
		email: '',
		firstName: '',
		lastName: '',
		query: '',
		bin: '',
		last4: '',
		types: '',
		mid: '',
	};
	savedOrderFilters(dispatch, data, orderStatuses);
};
